import { useSelector } from "react-redux";

import { countTotalRows } from "@/components/spatialAnalysis/catchmentDataSection/catchmentDataset";
import { getValueOfHighestLevelAdminArea } from "@/helpers/datasetAttributeHelper";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  AreaInputType,
  ECatchmentInputBy,
  EOutputAnalysisType,
  EOutputCoverageType,
  EOutputGridType
} from "@/types/spatial";

export type ValidationAnalysisConfigErrors = {
  datasetMessage?: string;
  areaInputMessage?: string;
  outputAnalysisMessage?: string;
  dataWeightMessage?: string;
  resolutionMessage?: string;
};

export const useValidationAnalysisConfigInput = () => {
  const { datasets } = useSelector((state: RootState) => state.dataset);
  const {
    isBVTDataForCatchmentEnabled,
    isCoverageTypeEnabled,
    isAdminTreeEnabled
  } = useAppFeatures();

  const spatial = useSelector((state: RootState) => state.spatial);

  const {
    adminAreaSettings,
    adminTreeSettings,
    catchmentSettings,
    polygonSettings,
    spatialConfig
  } = spatial;

  const isGridOutput = spatialConfig.outputType === EOutputAnalysisType.GRID;

  // Check if any dataset is available
  const hasAvailableDataset = Object.values(datasets)?.some(
    (item) => item?.data?.available === true
  );

  const datasetMessage = !hasAvailableDataset
    ? "Dataset not selected. Please add dataset first."
    : null;

  const isDefineAreaFilled = (() => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.ADMIN_AREA: {
        if (isAdminTreeEnabled) {
          return (adminTreeSettings?.selectedAreaIds ?? []).length > 0;
        }
        return !!getValueOfHighestLevelAdminArea(adminAreaSettings);
      }
      case AreaInputType.CATCHMENT: {
        const inputBy = catchmentSettings?.catchmentInputBy;
        if (inputBy === ECatchmentInputBy.BY_LOCATION) {
          return (catchmentSettings?.placeCoordinates || []).length > 0;
        } else if (inputBy === ECatchmentInputBy.BY_DATASET) {
          return (
            countTotalRows(catchmentSettings?.datasetConfig?.datasets ?? {}) > 0
          );
        } else if (
          isBVTDataForCatchmentEnabled &&
          inputBy === ECatchmentInputBy.BY_POLYGON
        ) {
          return Object.keys(polygonSettings).length > 0;
        }
        return false;
      }
      case AreaInputType.POLYGON:
        return Object.keys(polygonSettings).length > 0;
      default:
        return false;
    }
  })();

  const areaInputMessage =
    !spatialConfig.areaInput || !isDefineAreaFilled
      ? "Area not selected. Please choose an area first."
      : null;

  const outputAnalysisMessage =
    isGridOutput && !spatialConfig.gridType
      ? "Output type not selected. Please choose Geohash or H3."
      : null;

  const isGridTypeGeohash = spatialConfig.gridType === EOutputGridType.GEOHASH;
  const isGridTypeH3 = spatialConfig.gridType === EOutputGridType.H3;

  const isGeohashResolutionMissing =
    isGridTypeGeohash && !spatialConfig.gridResolution;
  const isH3ResolutionMissing = isGridTypeH3 && !spatialConfig.h3Resolution;

  const resolutionMessage =
    isGridOutput && (isGeohashResolutionMissing || isH3ResolutionMissing)
      ? "Resolution not selected. Please choose a resolution first."
      : null;

  const isCustomCatchment =
    isCoverageTypeEnabled &&
    spatialConfig.outputMode === EOutputCoverageType.CUSTOM_CATCHMENT;

  const radius = spatialConfig.customCatchmentRadius;

  // Empty = no value typed (cleared field). Zero is a real, typed value.
  const isRadiusEmpty = radius === null || radius === undefined;
  const isRadiusMissing = isRadiusEmpty || radius <= 0;

  // Generate Results requires a positive radius, so zero/empty both disable it.
  // (No inline message, so this is a boolean flag rather than an error message.)
  const isCoverageRadiusMissing =
    isGridOutput && isCustomCatchment && isRadiusMissing;

  // Save Project only requires the field to be filled, so zero is savable and
  // only an empty field disables it.
  const isCoverageRadiusEmpty =
    isGridOutput && isCustomCatchment && isRadiusEmpty;

  return {
    canGenerateAnalysisResults:
      !datasetMessage &&
      !areaInputMessage &&
      !outputAnalysisMessage &&
      !resolutionMessage &&
      !isCoverageRadiusMissing,
    isCoverageRadiusMissing,
    isCoverageRadiusEmpty,
    validationErrors: {
      datasetMessage,
      areaInputMessage,
      outputAnalysisMessage,
      resolutionMessage
    },
    hasAvailableDataset
  };
};
